import { useStoreContact } from "@/context/StoreContactContext";
import { useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Clock, Phone, Mail, CreditCard, Loader2, Navigation } from "lucide-react";
import { BRAND } from "@/lib/data";


export const Contact = () => {
  const contact = useStoreContact();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We've received your message and will get back soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(contact.phone ? "Something went wrong. Please try again or call us." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-foreground/20 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors duration-300";

  const info = [
    { icon: MapPin, label: "Visit", value: BRAND.address },
    { icon: Clock, label: "Hours", value: BRAND.hours },
    { icon: Phone, label: "Call", value: contact.phone, href: contact.phoneHref },
    { icon: Mail, label: "Email", value: contact.email, href: `mailto:${contact.email}` },
  ].filter((item) => item.value);

  return (
    <section
      id="contact"
      className="py-24 lg:py-32 max-w-7xl mx-auto px-6"
      data-testid="contact-section"
    >
      <div className="grid lg:grid-cols-2 gap-16">
        {/* Info */}
        <div>
          <p className="overline text-accent mb-6">05 / Visit Us</p>
          <h2 className="font-display text-4xl sm:text-6xl leading-none tracking-tight text-foreground">
            Come by, or <em className="italic text-accent">drop us a line.</em>
          </h2>

          <div className="mt-12 space-y-8">
            {info.map((it) => (
              <motion.div
                key={it.label}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex gap-5"
                data-testid={`contact-info-${it.label.toLowerCase()}`}
              >
                <div className="w-11 h-11 shrink-0 border border-border flex items-center justify-center text-accent">
                  <it.icon size={18} />
                </div>
                <div>
                  <div className="font-mono-label text-[0.65rem] uppercase tracking-widest text-muted-foreground mb-1.5">
                    {it.label}
                  </div>
                  {it.href ? (
                    <a
                      href={it.href}
                      className="text-foreground text-base leading-relaxed hover:text-accent transition-colors duration-300"
                    >
                      {it.value}
                    </a>
                  ) : (
                    <p className="text-foreground text-base leading-relaxed max-w-sm">
                      {it.value}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}

            <div className="flex gap-5" data-testid="contact-info-payments">
              <div className="w-11 h-11 shrink-0 border border-border flex items-center justify-center text-accent">
                <CreditCard size={18} />
              </div>
              <div>
                <div className="font-mono-label text-[0.65rem] uppercase tracking-widest text-muted-foreground mb-2">
                  We Accept
                </div>
                <div className="flex flex-wrap gap-2">
                  {BRAND.payments.map((p) => (
                    <span
                      key={p}
                      className="text-xs font-mono-label px-3 py-1.5 border border-border text-foreground"
                    >
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:pt-16">
          <div className="bg-white border border-border p-8 sm:p-10 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)]">
            <h3 className="font-display text-3xl text-foreground mb-8">
              Send a message
            </h3>
            <form onSubmit={submit} className="space-y-7" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-7">
                <input
                  className={inputCls}
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                  data-testid="contact-input-name"
                />
                <input
                  className={inputCls}
                  type="email"
                  required
                  placeholder="Email address"
                  value={form.email}
                  onChange={set("email")}
                  data-testid="contact-input-email"
                />
              </div>
              <input
                className={inputCls}
                placeholder="Subject (optional)"
                value={form.subject}
                onChange={set("subject")}
                data-testid="contact-input-subject"
              />
              <textarea
                className={`${inputCls} resize-none`}
                rows={4}
                placeholder="How can we help?"
                value={form.message}
                onChange={set("message")}
                data-testid="contact-input-message"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-3 bg-foreground text-primary-foreground py-4 text-xs font-mono-label uppercase tracking-widest hover:bg-accent transition-colors duration-300 disabled:opacity-60"
                data-testid="contact-submit-btn"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Map + Directions */}
      <div className="mt-16">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <p className="overline text-accent mb-3">Find Us</p>
            <h3 className="font-display text-3xl sm:text-4xl leading-none tracking-tight text-foreground">
              Dabok Chauraha, Udaipur
            </h3>
          </div>
          <a
            href={BRAND.directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-foreground text-primary-foreground px-7 py-3.5 text-xs font-mono-label uppercase tracking-widest hover:bg-accent transition-colors duration-300 self-start"
            data-testid="get-directions-btn"
          >
            <Navigation size={15} />
            Get Directions
          </a>
        </div>
        <div className="relative w-full h-[380px] sm:h-[440px] border border-border overflow-hidden grayscale-[0.15] hover:grayscale-0 transition-[filter] duration-500">
          <iframe
            title="DV Mart location map"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(BRAND.mapQuery)}&z=15&output=embed`}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="contact-map"
          />
        </div>
      </div>
    </section>
  );
};
